"use client"

import type React from "react";
import { routing } from "@/i18n/routing";
import { linkCategoryPath as serverLinkCategoryPath } from "./server-helper";

export const tryParse = (jsonString: string) => {
  try {
    return JSON.parse(jsonString)
  } catch (error) {
    return null
  }
}

/**
 * Helper function to build category path
 * Re-exported from server-helper for client components
 * @param url - URL string to process
 * @returns Formatted path
 */
export const linkCategoryPath = serverLinkCategoryPath

/**
 * Helper function to check if item is active based on pathname
 * @param itemUrl - URL of the menu item
 * @param pathname - Current pathname
 * @returns Whether the item is active
 */
export const isItemActive = (itemUrl: string, pathname: string) => {
  const itemPath = linkCategoryPath(itemUrl)
  const currentPath = pathname || "/"

  // If item is homepage, only active when current path is root
  if (itemPath === "/" || itemUrl.includes("homepage-vi")) {
    return currentPath === "/" || currentPath === "/vi" || currentPath === "/cs" || currentPath === "/en"
  }

  // Normalize paths for comparison (remove trailing slashes)
  const normalizedItemPath = itemPath.replace(/\/$/, "")
  const normalizedCurrentPath = currentPath.replace(/\/$/, "")

  // Check if current path exactly matches item path or starts with item path + "/"
  return normalizedCurrentPath === normalizedItemPath || normalizedCurrentPath.startsWith(normalizedItemPath + "/")
}

export interface MenuLikeItem {
  url: string
  attribute?: MenuLikeItem[]
}

/**
 * Like isItemActive, but also matches when the current path belongs to one of
 * the item's submenu children (recursively), so a parent nav item stays
 * highlighted while a child/grandchild category page is open.
 */
export const isMenuItemActive = (item: MenuLikeItem, pathname: string): boolean => {
  if (isItemActive(item.url, pathname)) return true
  return !!item.attribute?.some(child => isMenuItemActive(child, pathname))
}

/**
 * Helper function to check if a link is valid for navigation
 * A link is considered valid if it exists, is not "#", and is not empty after trimming
 * @param link - Link string to validate
 * @returns Whether the link is valid for navigation
 */
export const isValidLink = (link: string | null | undefined): boolean => {
  return !!(link && link !== "#" && link.trim() !== "")
}

/**
 * Helper function to set padding style using switch case
 * @param style - CSSProperties object to modify
 * @param paddingType - Type of padding: "top" | "right" | "bottom" | "left"
 * @param value - Padding value in pixels (number or undefined)
 */
export const setPaddingStyle = (
  style: React.CSSProperties,
  paddingType: "top" | "right" | "bottom" | "left",
  value: number | undefined,
) => {
  if (value === undefined) return

  switch (paddingType) {
    case "top":
      style.paddingTop = `${value}px`
      break
    case "right":
      style.paddingRight = `${value}px`
      break
    case "bottom":
      style.paddingBottom = `${value}px`
      break
    case "left":
      style.paddingLeft = `${value}px`
      break
    default:
      break
  }
}

export const formatModuleName = (moduleKey: string): string => {
  if (!moduleKey) return ""

  const rawName = moduleKey.includes("::") ? moduleKey.split("::")[1].split(".")[0] : moduleKey

  return rawName.charAt(0).toUpperCase() + rawName.slice(1)
}

export const isSpecialModule = (moduleKey: string): boolean => {
  return moduleKey === "api::post" || moduleKey === "api::page"
}

export const formatActionName = (action: string): string => {
  const map: Record<string, string> = {
    find: "Tìm",
    findOne: "Tìm một",
    create: "Tạo",
    update: "Cập nhật",
    delete: "Xóa",
    createLocalization: "Tạo bản đa ngôn ngữ",
    publish: "Xuất bản",
    requestReview: "Gửi duyệt bài",
    approve: "Duyệt bài",
    reject: "Từ chối duyệt bài",
    bulkDelete: "Xóa nhiều",
    export: "Xuất file",
    stats: "Thống kê",
    upload: "Tải lên",
    destroy: "Hủy/Xóa",
    sync: "Đồng bộ",
    send: "Gửi",
    manage: "Quản lý",
    getFolder: "Lấy thư mục",
    getFolders: "Lấy nhiều thư mục",
    createFolder: "Tạo thư mực",
    updateFolder: "Cập nhật thư mục",
    deleteFolder: "Xóa thư mục",
    getFolderFiles: "Lấy file của thư mục",
    uploadToFolder: "Tải vào thư mục",
    getFolderTree: "Lấy cây thư mục",
    moveFilesToFolder: "Di chuyển tệp tới thư mục",
    flushAll: "Xóa hết",
    flushByContentTypes: "Xóa hết bằng nhiều Content Type",
    flushByContentType: "Xóa hết bằng Content Type",
    getDocumentation: "Lấy tài liệu",
    getPostAuthor: "Lấy tác giả bài viết",
    checkSlug: "Kiểm tra đường dẫn",
    author: "Lấy tác giả",
    authors: "Lấy tất cả tác giả",
    getComponents: "Lấy tất cả component",
    getComponent: "Lấy 1 component",
    listLocales: "Lấy tất cả bản dịch",
    callback: "Gọi lại",
    changePassword: "Thay đổi mật khẩu",
    resetPassword: "Tạo lại mật khẩu",
    connect: "Kết nối",
    forgotPassword: "Quên mật khẩu",
    register: "Đăng ký",
    emailConfirmation: "Xác nhận email",
    sendEmailConfirmation: "Gửi mail xác nhận",
    getPermissions: "Lấy các quyền",
    createRole: "Tạo vai trò",
    updateRole: "Cập nhật vai trò",
    deleteRole: "Xóa vai trò",
    count: "Đếm",
    me: "Tôi",
    getContentTypes: "Lấy tất cả content type",
    getContentType: "Lấy một content type",
    findMine: "Tìm bài viết của tôi",
    findOneMine: "Tìm một bài viết của tôi",
    updateMine: "Cập nhật bài viết của tôi",
    deleteMine: "Xóa bài viết của tôi",
    submit: "Gửi yêu cầu xuất bản",
    unpublish: "Hủy xuất bản",
    schedule: "Lên lịch xuất bản"
  }
  return map[action] || action
}

export const getModuleLabel = (moduleKey: string, menuItems: any[]): string => {
  if (!moduleKey) return ""

  const cleanKey = moduleKey.replace("api::", "").replace("plugin::", "")

  // Recursive find — supports both flat and grouped menu structures.
  // After Lane C, top-level items are groups containing the real modules
  // in their `children`, so we must walk the tree.
  const findDeep = (items: any[]): any | null => {
    for (const item of items) {
      if (item.permission === cleanKey || item.key === cleanKey) return item
      if (Array.isArray(item.children) && item.children.length > 0) {
        const nested = findDeep(item.children)
        if (nested) return nested
      }
    }
    return null
  }

  const menuItem = findDeep(menuItems)
  if (menuItem) return menuItem.label

  return formatModuleName(moduleKey)
}

/**
 * TẠO ĐƯỜNG DẪN BAO GỒM LOCALE
 * @param slug - Đường dẫn bài viết hoặc danh mục
 * @param locale - Mã ngôn ngữ hiện tại (en, vi, ...)
 * @returns string
 */
export const getLocalizedHref = (slug?: string, locale?: string): string => {
  if (!slug) return "/";
  const cleanSlug = slug.startsWith("/") ? slug : `/${slug}`;
  // as-needed: locale mặc định (vi) KHÔNG prefix — nếu không middleware sẽ phải
  // redirect /vi/... về /... (thừa 1 hop) và dễ gây double-prefix khi ghép PostLink.
  if (!locale || locale === routing.defaultLocale) return cleanSlug;

  return `/${locale}${cleanSlug}`;
};

export const formatNumber = (value: number | string): string => {
  const number = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(number)) return "0";
  return new Intl.NumberFormat("vi-VN").format(number);
};

export const DEVELOPER_ONLY_MODULES = [
  "i18n",
  "swagger", 
  "content-type-builder",
  "post-view",
  "permission-mapping",
  "batch",
]

